import { useState, useEffect, useRef } from 'react'
import { Upload, Download, FileText, Settings, AlertCircle, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { downloadSampleCSV } from '../utils/sampleData'
import type { ApiCredentials } from '../types/ingestion.types'
import { INGESTION_API } from '@/api/client'

type EntityType = 'events' | 'customers' | 'transactions'

interface UploadResponse {
  status: 'SUCCESS' | 'PARTIAL_SUCCESS' | 'ERROR'
  message: string
  totalRecords?: number
  successCount?: number
  failureCount?: number
  errors?: Array<{
    line: number
    error: string
  }>
}

export function BulkUploadTester() {
  const [credentials, setCredentials] = useState<ApiCredentials | null>(null)
  const [showCredentialsForm, setShowCredentialsForm] = useState(false)
  const [entityType, setEntityType] = useState<EntityType>('events')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [response, setResponse] = useState<UploadResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load credentials from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('fh_api_credentials')
    if (stored) {
      try {
        setCredentials(JSON.parse(stored))
      } catch {
        // Invalid JSON, ignore
      }
    } else {
      setShowCredentialsForm(true)
    }
  }, [])

  const handleSaveCredentials = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const creds: ApiCredentials = {
      keyId: formData.get('keyId') as string,
      secret: formData.get('secret') as string,
      accountId: parseInt(formData.get('accountId') as string, 10),
    }
    localStorage.setItem('fh_api_credentials', JSON.stringify(creds))
    setCredentials(creds)
    setShowCredentialsForm(false)
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please select a CSV file')
        return
      }
      setSelectedFile(file)
      setError(null)
      setResponse(null)
    }
  }

  const handleUpload = async () => {
    if (!credentials) {
      setError('Please configure API credentials first')
      return
    }

    if (!selectedFile) {
      setError('Please select a file to upload')
      return
    }

    try {
      setIsUploading(true)
      setError(null)
      setResponse(null)

      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('accountId', credentials.accountId.toString())
      formData.append('entityType', entityType)

      const startTime = Date.now()

      const res = await fetch(`${INGESTION_API}/bulk/${entityType}`, {
        method: 'POST',
        headers: {
          'X-FH-Key-ID': credentials.keyId,
          'X-FH-Key-Secret': credentials.secret,
        },
        body: formData,
      })

      const duration = Date.now() - startTime
      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.message || `HTTP ${res.status}: ${res.statusText}`)
      }

      setResponse(data)

      // Save to request history
      const historyItem = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        method: 'POST' as const,
        endpoint: `/api/v1/bulk/${entityType}`,
        status: res.status,
        duration,
        request: {
          entityType,
          fileName: selectedFile.name,
          fileSize: selectedFile.size,
        },
        response: data,
      }
      const history = JSON.parse(localStorage.getItem('fh_request_history') || '[]')
      history.unshift(historyItem)
      localStorage.setItem('fh_request_history', JSON.stringify(history.slice(0, 50)))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred'
      setError(message)
    } finally {
      setIsUploading(false)
    }
  }

  const handleDownloadSample = () => {
    downloadSampleCSV(entityType)
  }

  const handleClearFile = () => {
    setSelectedFile(null)
    setResponse(null)
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  if (showCredentialsForm || !credentials) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Configure API Credentials</h3>
          <p className="text-sm text-blue-700">
            Enter your Ingestion API credentials to start testing. These will be stored locally in your browser.
          </p>
        </div>

        <form onSubmit={handleSaveCredentials} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account ID
            </label>
            <input
              type="number"
              name="accountId"
              required
              defaultValue={credentials?.accountId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="1234"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key ID
            </label>
            <input
              type="text"
              name="keyId"
              required
              defaultValue={credentials?.keyId}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="your-api-key-id"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Secret
            </label>
            <input
              type="password"
              name="secret"
              required
              defaultValue={credentials?.secret}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary font-mono text-sm"
              placeholder="your-api-secret"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              className="flex-1 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 transition-colors"
            >
              Save Credentials
            </button>
            {credentials && (
              <button
                type="button"
                onClick={() => setShowCredentialsForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Credentials Badge */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
            Account ID: {credentials.accountId}
          </div>
          <button
            onClick={() => setShowCredentialsForm(true)}
            className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <Settings className="h-4 w-4" />
            Update Credentials
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Upload Configuration */}
        <div className="space-y-4">
          {/* Entity Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Entity Type
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as EntityType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="events">Events</option>
              <option value="customers">Customers</option>
              <option value="transactions">Transactions</option>
            </select>
          </div>

          {/* Sample CSV Download */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Download className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="font-medium text-blue-900 mb-1">Need a sample CSV?</h4>
                <p className="text-sm text-blue-700 mb-3">
                  Download a sample CSV file with the correct format for {entityType}.
                </p>
                <button
                  onClick={handleDownloadSample}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm"
                >
                  Download Sample {entityType}.csv
                </button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload CSV File
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
                selectedFile
                  ? 'border-green-300 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              )}
            >
              {selectedFile ? (
                <div>
                  <FileText className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <p className="font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {(selectedFile.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleClearFile()
                    }}
                    className="mt-3 text-sm text-red-600 hover:text-red-700"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Click to select CSV file</p>
                  <p className="text-sm text-gray-500 mt-1">or drag and drop</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
            className={cn(
              'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md font-medium transition-colors',
              isUploading || !selectedFile
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary text-white hover:bg-primary/90'
            )}
          >
            <Upload className="h-5 w-5" />
            {isUploading ? 'Uploading...' : 'Upload File'}
          </button>
        </div>

        {/* Right Column - Response */}
        <div className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">
            Upload Results
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <div className="font-medium text-red-900">Error</div>
                  <p className="text-sm text-red-800 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {response && (
            <div
              className={cn(
                'border rounded-lg p-4',
                response.status === 'SUCCESS'
                  ? 'bg-green-50 border-green-200'
                  : response.status === 'PARTIAL_SUCCESS'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-red-50 border-red-200'
              )}
            >
              <div className="flex items-center gap-2 mb-3">
                {response.status === 'SUCCESS' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                )}
                <div
                  className={cn(
                    'px-2 py-1 rounded text-sm font-medium',
                    response.status === 'SUCCESS'
                      ? 'bg-green-100 text-green-800'
                      : response.status === 'PARTIAL_SUCCESS'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  )}
                >
                  {response.status}
                </div>
              </div>

              <div className="space-y-2">
                {response.message && (
                  <p className="text-sm font-medium">{response.message}</p>
                )}

                {response.totalRecords !== undefined && (
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <div className="text-gray-600">Total</div>
                      <div className="font-bold text-lg">{response.totalRecords}</div>
                    </div>
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <div className="text-gray-600">Success</div>
                      <div className="font-bold text-lg text-green-600">
                        {response.successCount || 0}
                      </div>
                    </div>
                    <div className="bg-white bg-opacity-50 rounded p-2">
                      <div className="text-gray-600">Failed</div>
                      <div className="font-bold text-lg text-red-600">
                        {response.failureCount || 0}
                      </div>
                    </div>
                  </div>
                )}

                {response.errors && response.errors.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium text-sm mb-2">Errors:</h4>
                    <div className="bg-white bg-opacity-50 rounded p-3 max-h-48 overflow-y-auto">
                      {response.errors.map((err, idx) => (
                        <div key={idx} className="text-sm mb-2 last:mb-0">
                          <span className="font-mono text-red-600">Line {err.line}:</span>{' '}
                          {err.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {!error && !response && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Upload a file to see the results</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
